import { BuilderDesigns } from '../customer-api/builder-designs';
import * as models from '../customer-api/models';
import { PurchasedDesignRecord, PurchasedDesigns } from './purchased-designs';

/**
 * One row of a builder page's previous-designs section: the customer's saved
 * designs (GET /builder-designs, signed in) merged with this browser's
 * purchase records (localStorage, any auth state), deduped, newest first.
 * UI-free — the section's DOM renders from these.
 *
 * Deliberately not re-exported from the customizer barrel — it pulls in
 * customer-api (axios + models), which engine-only consumers shouldn't pay
 * for (the share-design precedent).
 */
export interface PreviousDesignEntry {
    builderDesignID: string;
    /** Canonical CloudFront URL of the rendered preview, or empty (old designs have none) */
    previewURL: string;
    /** Query string (starting `?`) appended to the builder page's own path to reopen the design */
    reopenQuery: string;
    /** Epoch ms of the account design's last save; 0 for pure purchase records */
    savedAt: number;
    /** Epoch ms of the purchase; 0 for never-purchased designs */
    purchasedAt: number;
    /** Order quantity; 0 when not purchased */
    quantity: number;
    /** Set on purchased entries: the order key the order-status page takes as ?id= */
    orderKey: string;
    /** Set on purchased entries: pairs with orderKey for the anonymous by-order-line-item design fetch */
    orderLineItemID: string;
    /** Saved designs only (share links and owner actions like delete need it); empty for pure purchase records */
    designKey: string;
}

function entryFromDesign(design: models.BuilderDesign): PreviousDesignEntry {
    return {
        builderDesignID: design.id,
        previewURL: design.upload?.key ? design.upload.url() : '',
        reopenQuery: `?design=${encodeURIComponent(design.key)}`,
        savedAt: design.rowModified.isValid ? design.rowModified.toMillis() : 0,
        purchasedAt: 0,
        quantity: 0,
        orderKey: '',
        orderLineItemID: '',
        designKey: design.key,
    };
}

function sortKey(entry: PreviousDesignEntry): number {
    return Math.max(entry.savedAt, entry.purchasedAt);
}

// Merges the account's saved designs with this browser's purchase records.
// A purchased design the account also owns keeps the saved entry's reopen
// (latest version) and gains the purchase's date, quantity, and order; a
// purchase the account doesn't own (guest checkout) reopens by order line
// item, which works anonymously.
export function MergePreviousDesigns(designs: models.BuilderDesign[], purchased: PurchasedDesignRecord[]): PreviousDesignEntry[] {
    const entries = new Map<string, PreviousDesignEntry>();
    for (const design of designs) {
        entries.set(design.id, entryFromDesign(design));
    }
    for (const record of purchased) {
        const existing = record.builderDesignID ? entries.get(record.builderDesignID) : undefined;
        if (existing) {
            existing.purchasedAt = Math.max(existing.purchasedAt, record.purchasedAt);
            existing.quantity = record.quantity;
            existing.orderKey = record.orderKey;
            existing.orderLineItemID = record.orderLineItemID;
            if (!existing.previewURL) {
                existing.previewURL = record.previewURL;
            }
            continue;
        }
        entries.set(record.builderDesignID || record.orderLineItemID, {
            builderDesignID: record.builderDesignID,
            previewURL: record.previewURL,
            reopenQuery: `?order=${encodeURIComponent(record.orderKey)}&orderLineItem=${encodeURIComponent(record.orderLineItemID)}`,
            savedAt: 0,
            purchasedAt: record.purchasedAt,
            quantity: record.quantity,
            orderKey: record.orderKey,
            orderLineItemID: record.orderLineItemID,
            designKey: '',
        });
    }
    return [...entries.values()].sort((a, b) => sortKey(b) - sortKey(a));
}

export interface PreviousDesignsOptions {
    /** Restrict to one builder product's designs (the page's line item) */
    lineItemID?: string;
    /** Only a signed-in session may call the designs list (it 403s otherwise) */
    loggedIn: boolean;
}

export async function LoadPreviousDesigns(opts: PreviousDesignsOptions): Promise<PreviousDesignEntry[]> {
    const purchased = PurchasedDesigns().
        filter(record => !opts.lineItemID || record.lineItemID === opts.lineItemID);

    let designs: models.BuilderDesign[] = [];
    if (opts.loggedIn) {
        const resp = await BuilderDesigns({
            data: 'id,key,rowModified,uploadLineItem,upload',
            max: 50,
        });
        designs = resp.results.
            filter(design => !opts.lineItemID || design.uploadLineItem.lineItemID === opts.lineItemID);
    }

    return MergePreviousDesigns(designs, purchased);
}
